/**
 * WP-14 — Real platform scene-items / presets / package adapter.
 *
 * Ports the SDK-side wire implementation from
 * `app-platform-gtwin/packages/sdk-ts/src/sceneItems.ts` and the matching
 * `presets.{upsert,remove}` and `presets.activate` flows from
 * `scene.ts` into the viewer stub, so the viewer's
 * `SceneItemsAdapter` / `ScenePresetsAdapter` / `PackageAdapter`
 * contracts route through the real FastAPI endpoints documented in
 * `SCENE_ITEMS_SDK_SPEC.md` §2.3 when
 * `VITE_GTWIN_SCENE_ITEMS=real`.
 *
 * Why we port instead of taking a workspace `file:` dep on the platform
 * package: see `docs/results/WP-14-real-sdk-design-brief.md` §1.
 *
 * TODO(WP-14b): once a shared `pnpm-workspace.yaml` lands at the group
 * root, replace this file with a thin wrapper that delegates to
 * `@geosoft/gtwin-sdk`'s `client.scene.items.*` / `client.scene.presets.*`
 * / `client.package.exportGtwin` via an instance owned by
 * `gtwinClientSingleton.ts`. The drift test already enforces type-level
 * subset compatibility.
 */

import {
  AnchorConflictError,
  InvalidSceneItemError,
  NotAuthorizedError,
  NotFoundError,
  StaleParentVersionError,
} from './errors.js';
import type {
  PackageAdapter,
  PackageExportResult,
  SceneItemInput,
  SceneItemPatch,
  SceneItemsAdapter,
  ScenePresetInput,
  ScenePresetsAdapter,
} from './scene-items.js';
import type { SceneItem } from './types.js';
import { recordAudit } from './audit.js';

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

export interface RealSceneItemsAdapterOptions {
  readonly baseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly getToken?: () => Promise<string | undefined>;
  readonly defaultTenantId?: string;
  /**
   * Optional one-shot retry callback fired when the server rejects a
   * mutating call with `409 stale-parent-version`. Receives the freshly
   * loaded envelope; return a new `parentVersion` to retry once, or
   * `undefined` to surface the original `StaleParentVersionError`.
   * Mirrors the platform SDK's `StaleVersionRetry`.
   */
  readonly onStaleParent?: (current: {
    version: number;
    envelope: Record<string, unknown>;
  }) => number | undefined | Promise<number | undefined>;
}

export interface RealSceneItemsSurface {
  readonly items: SceneItemsAdapter;
  readonly presets: ScenePresetsAdapter;
  readonly package: PackageAdapter;
}

// ---------------------------------------------------------------------------
// Internal helpers — ported verbatim from
// `app-platform-gtwin/packages/sdk-ts/src/sceneItems.ts` so error
// classification matches what the platform Python service emits.
// ---------------------------------------------------------------------------

interface ServerErrorBody {
  error?: string;
  detail?: string | { code?: string; message?: string; fieldPath?: string };
  fieldPath?: string;
  code?: string;
  currentVersion?: number;
  envelope?: Record<string, unknown>;
  anchor?: Record<string, unknown>;
  attemptedCrs?: string;
  message?: string;
}

async function parseError(r: Response): Promise<ServerErrorBody> {
  const text = await r.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as ServerErrorBody;
  } catch {
    return { detail: text };
  }
}

function detailString(body: ServerErrorBody, fallback: string): string {
  const d = body.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  if (typeof body.message === 'string') return body.message;
  if (typeof body.error === 'string') return body.error;
  return fallback;
}

function detailFieldPath(body: ServerErrorBody): string | undefined {
  if (typeof body.fieldPath === 'string') return body.fieldPath;
  const d = body.detail;
  if (d && typeof d === 'object' && typeof d.fieldPath === 'string') {
    return d.fieldPath;
  }
  return undefined;
}

function detailCode(body: ServerErrorBody): string | undefined {
  if (typeof body.code === 'string') return body.code;
  if (typeof body.error === 'string') return body.error;
  const d = body.detail;
  if (d && typeof d === 'object' && typeof d.code === 'string') return d.code;
  return undefined;
}

async function throwForStatus(r: Response, gtwinId: string): Promise<void> {
  if (r.ok) return;
  const body = await parseError(r);
  const code = detailCode(body);
  if (r.status === 401 || r.status === 403) {
    throw new NotAuthorizedError(
      detailString(body, `tenant lacks scene-write on ${gtwinId}`),
    );
  }
  if (r.status === 404) {
    throw new NotFoundError(detailString(body, `gTwin ${gtwinId} not found`));
  }
  if (r.status === 409 && code === 'anchor-conflict') {
    throw new AnchorConflictError(
      detailString(body, 'scene item conflicts with promoted project anchor'),
      body.anchor,
      body.attemptedCrs,
    );
  }
  if (r.status === 409) {
    throw new StaleParentVersionError(
      detailString(body, 'stale parentVersion'),
      body.currentVersion,
      body.envelope,
    );
  }
  if (r.status === 400 && code === 'invalid-scene-item') {
    throw new InvalidSceneItemError(
      detailString(body, 'invalid scene item'),
      detailFieldPath(body),
    );
  }
  throw new Error(`request failed: ${r.status} ${detailString(body, '')}`.trim());
}

// ---------------------------------------------------------------------------
// HTTP client factory
// ---------------------------------------------------------------------------

interface FetchSnapshot {
  version: number;
  envelope: Record<string, unknown>;
}

interface InternalContext {
  readonly base: URL;
  readonly fetchImpl: typeof fetch;
  readonly tenant: string;
  readonly getToken: () => Promise<string | undefined>;
  readonly onStaleParent?: RealSceneItemsAdapterOptions['onStaleParent'];
}

function buildContext(options: RealSceneItemsAdapterOptions): InternalContext {
  // Bind to globalThis so the browser's `fetch` keeps its `this` context.
  const fetchImpl: typeof fetch =
    options.fetchImpl ?? ((...args) => fetch(...args));
  return {
    base: new URL(options.baseUrl),
    fetchImpl,
    tenant: options.defaultTenantId ?? 'dev-tenant',
    getToken: options.getToken ?? (() => Promise.resolve(undefined)),
    onStaleParent: options.onStaleParent,
  };
}

async function authHeaders(
  ctx: InternalContext,
  ifMatch?: string,
): Promise<Record<string, string>> {
  const token = await ctx.getToken();
  return {
    'content-type': 'application/json',
    'x-tenant-id': ctx.tenant,
    ...(token ? { authorization: `Bearer ${token}` } : {}),
    ...(ifMatch !== undefined ? { 'if-match': ifMatch } : {}),
  };
}

function endpoint(ctx: InternalContext, path: string): URL {
  return new URL(path, ctx.base);
}

async function fetchSnapshot(
  ctx: InternalContext,
  gtwinId: string,
): Promise<FetchSnapshot> {
  const r = await ctx.fetchImpl(
    endpoint(ctx, `api/v1/gtwins/${encodeURIComponent(gtwinId)}`),
    { method: 'GET', headers: await authHeaders(ctx) },
  );
  await throwForStatus(r, gtwinId);
  const row = (await r.json()) as Record<string, unknown>;
  return {
    version: row['version'] as number,
    envelope: (row['envelope'] as Record<string, unknown>) ?? {},
  };
}

/**
 * Run `attempt` with a parent version. If the server rejects with
 * `StaleParentVersionError` and the caller supplied an `onStaleParent`
 * callback (via options or per-call opts), refresh the parent version
 * and retry once. The retry never recurses.
 */
async function withConflictRetry<T>(
  ctx: InternalContext,
  attempt: (parentVersion: number) => Promise<T>,
  resolveParent: () => Promise<number>,
): Promise<T> {
  const parentVersion = await resolveParent();
  try {
    return await attempt(parentVersion);
  } catch (err) {
    if (
      err instanceof StaleParentVersionError &&
      ctx.onStaleParent !== undefined &&
      err.currentVersion !== undefined
    ) {
      const next = await ctx.onStaleParent({
        version: err.currentVersion,
        envelope: err.currentEnvelope ?? {},
      });
      if (typeof next === 'number') {
        return attempt(next);
      }
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Mapping helpers — the viewer's `SceneItemsAdapter.add` returns the
// viewer-shaped `SceneItem` (with a flat `endpoints: Record<string,string>`
// and a `style` projection of slot / artifactKind / readiness /
// intelligenceLevel / provenance), while the platform `SceneItemRecord`
// is the verbose `SceneItemInput`-shaped record. We project the platform
// response down to the viewer shape so consumer code (TwinShell,
// SceneTreePanel, sceneItemToLayer) stays unchanged.
// ---------------------------------------------------------------------------

function toViewerSceneItem(
  input: SceneItemInput,
  record?: Partial<{
    visible: boolean;
    opacity: number;
    style: Record<string, unknown>;
  }>,
): SceneItem {
  const endpoints: Record<string, string> = {};
  for (const [key, value] of Object.entries(input.endpoints)) {
    if (value && typeof value.href === 'string') endpoints[key] = value.href;
  }
  const style: Record<string, unknown> = {
    title: input.title,
    artifactKind: input.artifactKind,
    slot: input.slot,
    provenance: input.provenance,
    readiness: input.readiness ?? ['visualization-ready'],
    intelligenceLevel: input.intelligenceLevel ?? 'L1',
    ...(input.extensions ?? {}),
    ...(record?.style ?? {}),
  };
  if (record?.opacity !== undefined) style.opacity = record.opacity;
  return {
    id: input.id,
    layerKind: input.layerKind,
    crs: input.sourceCrs,
    bbox: input.bbox,
    endpoints,
    visible: record?.visible ?? true,
    style,
  };
}

// ---------------------------------------------------------------------------
// Public factory
// ---------------------------------------------------------------------------

export function createRealSceneItemsSurface(
  options: RealSceneItemsAdapterOptions,
): RealSceneItemsSurface {
  const ctx = buildContext(options);

  // -------------------------------------------------------------------------
  // SceneItemsAdapter
  // -------------------------------------------------------------------------

  const items: SceneItemsAdapter = {
    async add(gtwinId, item) {
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(ctx, `api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/items`),
            {
              method: 'POST',
              headers: await authHeaders(ctx),
              body: JSON.stringify({ parentVersion, item }),
            },
          );
          await throwForStatus(r, gtwinId);
          return (await r.json()) as {
            version: number;
            item: SceneItemInput & {
              visible?: boolean;
              opacity?: number;
              style?: Record<string, unknown>;
            };
          };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.items.add',
        target: item.id,
        newVersion: result.version,
        details: {
          slot: item.slot,
          layerKind: item.layerKind,
          artifactKind: item.artifactKind,
        },
      });
      return toViewerSceneItem(item, {
        visible: result.item.visible,
        opacity: result.item.opacity,
        style: result.item.style,
      });
    },

    async remove(gtwinId, itemId) {
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(
              ctx,
              `api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/items/${encodeURIComponent(itemId)}`,
            ),
            {
              method: 'DELETE',
              headers: await authHeaders(ctx, String(parentVersion)),
            },
          );
          await throwForStatus(r, gtwinId);
          if (r.status === 204) return { version: parentVersion + 1 };
          return (await r.json()) as { version: number };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.items.remove',
        target: itemId,
        newVersion: result.version,
      });
    },

    async patch(gtwinId, patches) {
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(ctx, `api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/items`),
            {
              method: 'PATCH',
              headers: await authHeaders(ctx),
              body: JSON.stringify({ parentVersion, patches }),
            },
          );
          await throwForStatus(r, gtwinId);
          return (await r.json()) as {
            version: number;
            items: Array<
              SceneItemInput & {
                visible?: boolean;
                opacity?: number;
                style?: Record<string, unknown>;
              }
            >;
          };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.items.patch',
        target: patches.map((p) => p.id).join(','),
        newVersion: result.version,
        details: { count: patches.length },
      });
      const byId = new Map<string, SceneItemPatch>();
      for (const patch of patches) byId.set(patch.id, patch);
      return result.items.map((record) =>
        toViewerSceneItem(record, {
          visible: record.visible ?? byId.get(record.id)?.visible,
          opacity: record.opacity ?? byId.get(record.id)?.opacity,
          style: record.style,
        }),
      );
    },

    async reorder(gtwinId, layerOrder) {
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(
              ctx,
              `api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/layer-order`,
            ),
            {
              method: 'PATCH',
              headers: await authHeaders(ctx),
              body: JSON.stringify({ parentVersion, layerOrder }),
            },
          );
          await throwForStatus(r, gtwinId);
          if (r.status === 204) return { version: parentVersion + 1 };
          return (await r.json()) as { version: number };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.items.reorder',
        target: layerOrder.join(','),
        newVersion: result.version,
      });
    },

    async copyFrom(targetGtwinId, input) {
    const r = await ctx.fetchImpl(
      endpoint(
        ctx,
        `api/v1/gtwins/${encodeURIComponent(targetGtwinId)}/scene/items/copy-from`,
      ),
      {
        method: 'POST',
        headers: await authHeaders(ctx),
        body: JSON.stringify({
          sourceGtwinId: input.sourceGtwinId,
          itemIds: input.itemIds ?? undefined,
          deleteFromSource: input.deleteFromSource ?? false,
          updatedBy: input.updatedBy ?? 'geoviewer',
        }),
      },
    );
    await throwForStatus(r, targetGtwinId);
    const body = (await r.json()) as {
      copied: number;
      items: string[];
      targetVersion: number;
      deleteFromSource: boolean;
    };
    recordAudit({
      gtwinId: targetGtwinId,
      action: 'scene.items.copy-from',
      target: input.sourceGtwinId,
      newVersion: body.targetVersion,
      details: {
        copiedItemIds: body.items,
        deleteFromSource: body.deleteFromSource,
      },
    });
    return {
      copied: body.copied,
      items: body.items,
      targetVersion: body.targetVersion,
      deleteFromSource: body.deleteFromSource,
    };
    },
  };

  // -------------------------------------------------------------------------
  // ScenePresetsAdapter
  // -------------------------------------------------------------------------

  const presets: ScenePresetsAdapter = {
    async activate(gtwinId, presetId) {
      // The platform SDK routes activate through the PATCH-ops surface
      // (set `payload.geospatial.activeScenePreset`). We mirror that here
      // so the real backend stays canonical.
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(ctx, `api/v1/gtwins/${encodeURIComponent(gtwinId)}`),
            {
              method: 'PATCH',
              headers: await authHeaders(ctx),
              body: JSON.stringify({
                parentVersion,
                ops: [
                  {
                    op: 'set',
                    path: 'payload.geospatial.activeScenePreset',
                    value: presetId,
                  },
                ],
              }),
            },
          );
          await throwForStatus(r, gtwinId);
          return (await r.json()) as { version: number };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.presets.activate',
        target: presetId,
        newVersion: result.version,
      });
    },

    async upsert(gtwinId, preset) {
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(
              ctx,
              `api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/presets/${encodeURIComponent(preset.id)}`,
            ),
            {
              method: 'PUT',
              headers: await authHeaders(ctx),
              body: JSON.stringify({ parentVersion, preset }),
            },
          );
          await throwForStatus(r, gtwinId);
          return (await r.json()) as { version: number; preset: ScenePresetInput };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.presets.upsert',
        target: preset.id,
        newVersion: result.version,
        details: {
          layerCount: Object.keys(preset.layerVisibility ?? {}).length,
        },
      });
      return result.preset;
    },

    async remove(gtwinId, presetId) {
      const result = await withConflictRetry(
        ctx,
        async (parentVersion) => {
          const r = await ctx.fetchImpl(
            endpoint(
              ctx,
              `api/v1/gtwins/${encodeURIComponent(gtwinId)}/scene/presets/${encodeURIComponent(presetId)}`,
            ),
            {
              method: 'DELETE',
              headers: await authHeaders(ctx, String(parentVersion)),
            },
          );
          await throwForStatus(r, gtwinId);
          if (r.status === 204) return { version: parentVersion + 1 };
          return (await r.json()) as { version: number };
        },
        async () => (await fetchSnapshot(ctx, gtwinId)).version,
      );
      recordAudit({
        gtwinId,
        action: 'scene.presets.remove',
        target: presetId,
        newVersion: result.version,
      });
    },
  };

  // -------------------------------------------------------------------------
  // PackageAdapter
  // -------------------------------------------------------------------------

  const packageAdapter: PackageAdapter = {
    async exportGtwin(gtwinId, exportOptions) {
      const mode = exportOptions?.mode ?? 'thin';
      const body: { mode?: 'thin' | 'fat' } = { mode };
      const r = await ctx.fetchImpl(
        endpoint(
          ctx,
          `api/v1/gtwins/${encodeURIComponent(gtwinId)}/package/export`,
        ),
        {
          method: 'POST',
          headers: await authHeaders(ctx),
          body: JSON.stringify(body),
        },
      );
      await throwForStatus(r, gtwinId);
      const parsed = (await r.json()) as PackageExportResult;
      // Server reports total bytes inline; emit one progress tick so
      // ExportGtwinAction can show the final size before the user clicks
      // download.
      if (typeof parsed.bytes === 'number' && exportOptions?.onProgress) {
        exportOptions.onProgress(parsed.bytes, parsed.bytes);
      }
      recordAudit({
        gtwinId,
        action: 'package.export',
        target: gtwinId,
        details: { mode, bytes: parsed.bytes, sha256: parsed.sha256 },
      });
      return parsed;
    },
  };

  return { items, presets, package: packageAdapter };
}
